import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { saveTransport } from "../../../services/transport-services";
import { formStateHandler, isEmpty } from "../../../utils/utils";
import { transformPayload } from "../TransportConstants";
import classes from "./index.module.css";
import { isValidPhoneNumber } from "../../../utils/utils";
import { fetchBranches } from "../../../services/branch-service";
import Checkbox from "../../../framework/Checkbox";
import ChipInputSelect from "../../../framework/ChipInputSelect";

import { TransportModes } from "../../../utils/constant";

const AddTransport = ({
  id,
  onSaveTransport = () => {},
  onEscape = () => {},
  transportData = null,
}) => {
  const [formState, setFormState] = useState({});
  const transportNameRef = useRef("");
  const minWeightRef = useRef("");
  //const vehicleNoRef = useRef("");
  const transporterIdRef = useRef("");
  const transportAddressRef = useRef("");
  const buildingNameRef = useRef("");
  const pinCodeRef = useRef("");
  const mobileNumberRef = useRef("");
  const emailRef = useRef("");
  const transportCodeRef = useRef("");
  const branchRef = useRef("");

  const { setCurrentComponent } = useContext(GlobalContext);
  const saveTransportRef = useRef("");
  const [branches, setBranches] = useState([]);

  const withPhoneCap = ({ value }) => value.length <= 10;
  const withPincodeCap = ({ value }) => value.length <= 6;

  const validateGstin = useCallback((gstin) => {
    const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
    if (!gstinRegex.test(gstin)) {
      displayToast({
        type: "error",
        title: "Please enter a valid GSTIN.",
      });
      return false;
    }
    // displayToast({
    //   type: "success",
    //   title: "GSTIN is valid.",
    // });
    return true;
  }, []);

  useEffect(() => {
    fetchBranches(`deleted=false`).then((r) => {
      const opts = r.result.rows.map((x) => {
        return {
          ...x,
          id: x._id,
          label: x.code,
        };
      });
      const sortedOpts = opts?.sort((x, y) => (x.region > y.region ? 1 : -1));
      setBranches(sortedOpts);
    });
  }, []);

  const validateForm = useCallback(
    async (fieldName, data) => {
      if (fieldName === "shortCode" || !fieldName) {
        if (!data?.shortCode) {
          transportCodeRef.current?.focus();
          return displayToast({
            type: "error",
            title: "Please enter Short Code",
          });
        }
      }

      if (fieldName === "transportName" || !fieldName) {
        if (!data?.transportName) {
          transportNameRef.current?.focus();
          return displayToast({
            type: "error",
            title: "Please enter Transporter Name",
          });
        }
      }

      if (fieldName === "gstin" || !fieldName) {
        if (!data?.gstin) {
          transporterIdRef.current?.focus();
          return displayToast({
            type: "error",
            title: "Please enter Transporter Id",
          });
        }

        if (!validateGstin(data?.gstin)) {
          return false;
        }
      }

      if (fieldName === "transportMode" || !fieldName) {
        if (!data?.transportMode) {
          transportCodeRef.current?.focus();
          return displayToast({
            type: "error",
            title: "Please select Transport Mode",
          });
        }
      }

      if (fieldName === "mobileNumber" || !fieldName) {
        if (data?.mobileNumber && !isValidPhoneNumber(data.mobileNumber)) {
          return displayToast({
            type: "error",
            title: "Please enter a valid 10 digit mobile number.",
          });
        }
      }

      if (fieldName === "email" || !fieldName) {
        const regex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (
          data?.email &&
          data.email.trim() !== "" &&
          !regex.test(data.email)
        ) {
          displayToast({
            type: "error",
            title: "Please enter a valid email.",
          });
          emailRef.current?.focus({ preventScroll: true });
          return false;
        }
      }

      if (fieldName === "branch" || !fieldName) {
        if (!data?.branch || data.branch.length === 0) {
          return displayToast({
            type: "error",
            title: "Please select Branch",
          });
        }
      }

      return true;
    },
    [validateGstin]
  );

  useEffect(() => {
    if (!formState.transportMode) {
      setFormState((prev) => ({
        ...prev,
        transportMode:
          TransportModes.find((mode) => mode.id === 1) || prev.transportMode,
      }));
    }
  }, [branches, formState, transportData]);

  const updateTransportHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({ ...formState });
      const response = await saveTransport(payload);
      if (response.status === "success") {
        onSaveTransport(response);
        return displayToast({
          type: "success",
          title: "Transporter added successfully.",
        });
      }
    },
    [formState, onSaveTransport, validateForm]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveTransportRef.current.click();
      }
    },
    [onEscape]
  );

  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (transportData) {
      setFormState({
        ...transportData,
        transportMode: TransportModes.find(
          (mode) => mode.label === transportData.transportMode
        ),
        branch: transportData.branch
          .map((branchCode) => branches.find((b) => b.code === branchCode))
          .filter((branch) => branch !== undefined),
      });
    }
  }, [branches, transportData]);

  return (
    <div>
      <h4 className={classes["transport-detail-label"]}>Transport Details</h4>
      <Grid container spacing={2} sx={{ p: "25px 20px 0px 30px" }}>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_code"
            name="transportCode"
            label="Short Code"
            placeholder="Enter Short Code"
            ref={transportCodeRef}
            value={formState?.shortCode || ""}
            onFocus={() => setCurrentComponent("add_transport_code")}
            autoFocus
            onChange={(e) => handleFormChange("shortCode", e.target.value)}
            //onBlur={() => validateForm("transportCode", formState)}
            onBlur={() => {
              if (formState.transportCode) validateForm("shortCode", formState);
            }}
            required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_code",
                left: "add_transport_code",
                right: "add_transport_name",
                down: "add_transport_id",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_name"
            name="transportName"
            label="Transporter Name"
            placeholder="Enter Transporter Name"
            ref={transportNameRef}
            value={formState?.transportName || ""}
            onFocus={() => setCurrentComponent("add_transport_name")}
            onChange={(e) => {
              handleFormChange("transportName", e.target.value);
            }}
            onBlur={() => {
              if (formState.transportName)
                validateForm("transportName", formState);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_name",
                left: "add_transport_code",
                right: "add_transport_id",
                down: "add_transport_mode",
              }),
            }}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_id"
            name="transporterId"
            label="Transporter ID (GSTIN)"
            placeholder="Enter Transporter GSTIN"
            ref={transporterIdRef}
            value={formState?.gstin || ""}
            onFocus={() => setCurrentComponent("add_transport_id")}
            onChange={(e) => {
              handleFormChange("gstin", e.target.value);
            }}
            onBlur={() => {
              if (formState.gstin) validateForm("gstin", formState);
            }}
            required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_code",
                left: "add_transport_name",
                right: "add_transport_mode",
                down: "add_transport_address",
              }),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectInput
            id="add_transport_mode"
            label="Transport Mode"
            value={formState?.transportMode}
            onChange={(e, value) => {
              handleFormChange("transportMode", value);
            }}
            onBlur={() => {
              if (formState.transportMode)
                validateForm("transportMode", formState);
            }}
            options={TransportModes}
            disableClearable={false}
            required
            onFocus={() => setCurrentComponent("add_transport_mode")}
            markers={JSON.stringify({
              up: "add_transport_name",
              left: "add_transport_id",
              right: "add_transport_address",
              down: "add_transport_building_name",
            })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_address"
            name="transportAddress"
            label="Transport Address"
            placeholder="Enter Transport Address"
            ref={transportAddressRef}
            value={formState?.transportAddress || ""}
            onFocus={() => setCurrentComponent("add_transport_address")}
            onChange={(e) => {
              handleFormChange("transportAddress", e.target.value);
            }}
            onBlur={() => {
              if (formState.transportAddress)
                validateForm("transportAddress", formState);
            }}
            //required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_id",
                left: "add_transport_mode",
                right: "add_transport_building_name",
                down: "add_transport_pin_code",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_building_name"
            name="buildingName"
            label="Building Name"
            placeholder="Enter Building Name"
            ref={buildingNameRef}
            value={formState?.buildingName || ""}
            onFocus={() => setCurrentComponent("add_transport_building_name")}
            onChange={(e) => {
              handleFormChange("buildingName", e.target.value);
            }}
            //required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_mode",
                left: "add_transport_address",
                right: "add_transport_pin_code",
                down: "add_transport_email",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            id="add_transport_pin_code"
            name="pinCode"
            label="Pin Code"
            placeholder="Enter Pin Code"
            ref={pinCodeRef}
            isAllowed={withPincodeCap}
            value={formState?.pincode || ""}
            onFocus={() => setCurrentComponent("add_transport_pin_code")}
            onChange={(e) => {
              handleFormChange("pincode", e.target.value);
            }}
            onBlur={() => {
              if (formState.pinCode) validateForm("pincode", formState);
            }}
            //required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_address",
                down: "add_transport_mobile_number",
                right: "add_transport_email",
                left: "add_transport_building_name",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="add_transport_email"
            name="Email"
            label="Email"
            placeholder="Enter your Email"
            ref={emailRef}
            value={formState?.email}
            onFocus={() => setCurrentComponent("add_transport_email")}
            onChange={(e) => {
              handleFormChange("email", e.target.value);
            }}
            //required
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_building_name",
                left: "add_transport_pin_code",
                right: "add_transport_mobile_number",
                down: "add_transport_min_weight",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            id="add_transport_mobile_number"
            name="mobileNumber"
            label="Mobile Number"
            placeholder="Enter Mobile Number"
            ref={mobileNumberRef}
            isAllowed={withPhoneCap}
            value={formState?.mobileNumber || ""}
            onFocus={() => setCurrentComponent("mobile_number")}
            onChange={(e) => {
              handleFormChange("mobileNumber", e.target.value);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_pin_code",
                down: "add_transport_mobile_number",
                right: "add_transport_min_weight",
                left: "add_transport_email",
              }),
            }}
            //required
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            id="add_transport_min_weight"
            name="minWeight"
            label="Min Weight"
            placeholder="Enter Min Weight"
            ref={minWeightRef}
            allowNegative={false}
            value={formState?.minWeight || ""}
            onFocus={() => setCurrentComponent("add_transport_min_weight")}
            decimalScale={3}
            isAllowed={(data) => {
              return data.value >= 0;
            }}
            onChange={(e) => {
              handleFormChange("minWeight", +e.target.value);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_transport_email",
                down: "add_transport_branch",
                right: "add_transport_branch",
                left: "add_transport_mobile_number",
              }),
            }}
            //required
          />
        </Grid>
        <Grid item xs={12}>
          <ChipInputSelect
            id="add_transport_branch"
            value={formState?.branch}
            options={branches}
            groupBy={(option) => option.region}
            name="branch"
            required={true}
            ref={branchRef}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            onFocus={() => {
              setCurrentComponent("add_transport_branch");
            }}
            onChange={(event, option) => {
              handleFormChange("branch", option);
              // setBranch(option)
            }}
            openOnFocus={true}
            label="Branches"
            multiple={true}
            blurOnSelect={false}
            disableCloseOnSelect={true}
            renderGroup={(params) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    padding: "5px",
                    gap: "5px",
                  }}
                  key={params.key}
                >
                  <div
                    style={{
                      height: "30px",
                      width: "100%",
                      background: "#e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      padding: "5px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {params.group}
                  </div>
                  <div>{params.children}</div>
                </div>
              );
            }}
            renderOption={(props, option, { selected }) => {
              return (
                <div
                  {...props}
                  style={{
                    display: "flex",
                    height: "40px",
                    width: "100%",
                    padding: "1px 5px",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                    }}
                  >
                    <Checkbox checked={selected} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItem: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        height: "15px",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      {option.name} ({option.code})
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option.address}
                    </div>
                  </div>
                </div>
              );
            }}
            markers={JSON.stringify({
              up: "add_transport_min_weight",
              left: "add_transport_min_weight",
              right: "add_transport_branch",
              down: "add_transport_branch",
            })}
          />
        </Grid>
      </Grid>
      <div className={classes["add-transport-save-button-wrapper"]}>
        <Button
          id="add_transport_save_button"
          ref={saveTransportRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Transport"
          color="violet"
          onClick={updateTransportHandler}
        />
      </div>
    </div>
  );
};

export default AddTransport;
